/*
  Toy Rocket — inverted problem tree.
  The root problem sits on top and the DAG grows downward like an upside-down
  tree: every parent is linked to its children with branch lines, branches
  collapse into a "+N" toggle, and each card rolls up the number of open and
  in-progress (claimed) problems in its whole subtree so the totals travel
  all the way to the root.

  This controller is host-agnostic. It talks to a `ProblemTreeHost` and never
  to `@napplet/sdk` directly, so the same UI runs as a sandboxed napplet
  (`src/napplet.ts`, shell-owned outbox + intent) and as a standalone Vite site
  (`src/web.ts`, its own default relays, read-only).
*/
import { gsap } from "gsap";
import "./styles.css";
import { assertRootCoordinate, buildProblemDag, subtreeStatusCounts, type ProblemDag, type RootSummary } from "./core/problem-dag";
import { mergeProblemEvents } from "./core/problem-events";
import { rocketsByProblem, rocketsFromEvents, type Rocket } from "./core/rockets";
import { applyZoom, escapeHtml, pulseToggle, reducedMotion, renderTree, type TreeViewState } from "./core/tree-view";
import type { EventSubscription, RelayEventResult } from "./core/nostr";
import type { HostCapabilities, ProblemTreeHost } from "./host/types";

export interface AppOptions {
  /** Root to grow on boot. Without one the setup screen is shown. */
  initialRoot?: string;
}

export function startProblemTreeApp(host: ProblemTreeHost, options: AppOptions = {}) {
  const app = (() => {
    const element = document.querySelector<HTMLElement>("#app");
    if (!element) throw new Error("Application root is missing.");
    return element;
  })();

  let dag: ProblemDag | undefined;
  let selected = "";
  const collapsed = new Set<string>();
  let zoom = 1;
  let capabilities: HostCapabilities = { viewProblem: false, composeProblem: false, viewRocket: false };
  let childComposerBusy = false;
  let problemViewerBusy = false;
  let rocketViewerBusy = false;
  /** Rockets indexed by the problem coordinate each one says it is solving. */
  let rocketsForProblems = new Map<string, Rocket[]>();
  let problemEvents: RelayEventResult[] = [];
  let problemSubscription: EventSubscription | undefined;
  let loadGeneration = 0;
  let drawerOpen = false;
  let rootSummaries: RootSummary[] | undefined;
  let rootSearch = "";
  let rootsLoading = false;
  let rootsError = "";
  let readOnlyNoticeShown = false;

  /** The napplet hides compose affordances it cannot dispatch; the web build never shows them. */
  const canCompose = () => capabilities.composeProblem;
  const status = () => document.querySelector<HTMLOutputElement>("#app-status");

  function say(message: string) {
    const output = status();
    if (output) output.textContent = message;
  }

  function drawerMarkup() {
    return `
    <div class="drawer-backdrop" hidden></div>
    <aside class="drawer" aria-label="Problem browser" hidden>
      <header class="drawer-header">
        <h2>Problems</h2>
        <button id="drawer-close" type="button" aria-label="Close problem browser">×</button>
      </header>
      <input id="root-search" type="search" placeholder="Filter problem trees" aria-label="Filter problem trees"
        autocomplete="off" spellcheck="false" value="${escapeHtml(rootSearch)}">
      <ul id="root-list" class="root-list"></ul>
      <div class="drawer-manual">
        <label for="drawer-coordinate">Load any root a-tag</label>
        <div class="drawer-coordinate-entry">
          <input id="drawer-coordinate" autocomplete="off" spellcheck="false" placeholder="31971:owner:problem-id">
          <button id="drawer-load" type="button">Load</button>
        </div>
      </div>
    </aside>`;
  }

  function showSetup(message = "") {
    stopProblemSubscription();
    const reopenDrawer = drawerOpen;
    drawerOpen = false;
    app.innerHTML = `
    <section class="setup" aria-labelledby="setup-title">
      <div class="setup-tree" aria-hidden="true">
        <i class="n root"></i><i class="e"></i><i class="e"></i>
        <i class="n"></i><i class="n"></i><i class="e"></i><i class="e"></i><i class="e"></i>
        <i class="n leaf"></i><i class="n leaf"></i><i class="n leaf"></i>
      </div>
      <div class="setup-copy">
        <h1 id="setup-title">Toy Rocket</h1>
        <p>Enter a full root problem coordinate, or browse the problem trees already published on ${escapeHtml(host.sourceLabel)} and pick one. The tree loads every problem below that root and grows it downward, root on top, leaves at the bottom.</p>
      </div>
      <div id="root-form">
        <label for="root-coordinate">Root problem a-tag</label>
        <div class="coordinate-entry">
          <input id="root-coordinate" name="root" required autocomplete="off" spellcheck="false"
            placeholder="31971:owner-pubkey:problem-id" aria-describedby="setup-status">
          <button type="button">Grow tree</button>
        </div>
        <output id="setup-status" class="setup-status" aria-live="polite">${escapeHtml(message)}</output>
      </div>
      <div class="setup-browse">
        <button id="browse-problems" type="button" aria-haspopup="dialog">Browse problems</button>
        <span class="setup-browse-hint">No problem id handy? Pick an existing problem tree from ${escapeHtml(host.sourceLabel)}.</span>
      </div>
    </section>${drawerMarkup()}`;
    bindWorkspace();
    const input = document.querySelector<HTMLInputElement>("#root-coordinate");
    const submit = document.querySelector<HTMLButtonElement>("#root-form button");
    const loadRoot = () => {
      if (input) void loadDag(input.value);
    };
    submit?.addEventListener("click", loadRoot);
    input?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      loadRoot();
    });
    if (reopenDrawer) setDrawer(true);
    gsap.fromTo(".setup > *", { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: .45, stagger: .07, ease: "expo.out" });
  }

  function stopProblemSubscription() {
    loadGeneration += 1;
    problemSubscription?.close();
    problemSubscription = undefined;
  }

  function receiveProblemEvents(rootCoordinate: string, incoming: RelayEventResult[]) {
    const merged = mergeProblemEvents(problemEvents, incoming);
    problemEvents = merged.events;
    if (!merged.changed || !dag || dag.rootCoordinate !== rootCoordinate) return;
    dag = buildProblemDag(rootCoordinate, problemEvents);
    if (!dag.nodes.has(selected)) selected = rootCoordinate;
    for (const coordinate of collapsed) {
      if (!dag.nodes.has(coordinate)) collapsed.delete(coordinate);
    }
    renderHud();
    renderTreeView(false);
  }

  function treeState(): TreeViewState {
    return { dag: dag!, collapsed, selected, rocketsByProblem: rocketsForProblems };
  }

  function treeCanvas() {
    return document.querySelector<HTMLElement>("#tree-canvas");
  }

  function renderTreeView(animate = true, animateCoordinate = "", center = false) {
    if (!dag) return;
    const canvas = treeCanvas();
    if (!canvas) return;
    renderTree(canvas, treeState(), { animate, animateCoordinate, zoom, center });
  }

  function renderHud() {
    if (!dag) return;
    const root = dag.nodes.get(dag.rootCoordinate);
    const selectedNode = dag.nodes.get(selected);
    const totals = subtreeStatusCounts(dag, dag.rootCoordinate);
    const rootTitle = document.querySelector<HTMLElement>("#root-title");
    const totalOpen = document.querySelector<HTMLElement>("#total-open");
    const totalProgress = document.querySelector<HTMLElement>("#total-progress");
    const logChildButton = document.querySelector<HTMLButtonElement>("#log-child");
    const logRootButton = document.querySelector<HTMLButtonElement>("#log-root-child");
    if (rootTitle && root) rootTitle.textContent = root.title;
    if (totalOpen) totalOpen.innerHTML = `<b>${totals.open}</b> open`;
    if (totalProgress) totalProgress.innerHTML = `<b>${totals.claimed}</b> in progress`;
    if (logChildButton) {
      logChildButton.disabled = !canCompose() || childComposerBusy || !selectedNode;
      logChildButton.title = !capabilities.composeProblem
        ? "No compatible problem composer available"
        : selectedNode
          ? `Log a child problem under ${selectedNode.title}`
          : "Select a problem first";
    }
    if (logRootButton) {
      logRootButton.disabled = !canCompose() || childComposerBusy;
      logRootButton.title = capabilities.composeProblem
        ? "Create a problem directly under the tree root"
        : "No compatible problem composer available";
    }
  }

  function renderApp(animate = true) {
    if (!dag) return;
    // The fresh drawer markup always starts hidden, so drop the stale open flag.
    drawerOpen = false;
    // The standalone build has nothing to dispatch intents to, so it does not
    // advertise compose buttons that could never do anything.
    const composeActions = host.kind === "web" ? "" : `
          <button id="log-child" type="button">Log child problem</button>
          <button id="log-root-child" type="button">New top-level problem</button>`;
    app.innerHTML = `
    <div class="shell">
      <header class="hud">
        <button id="menu-button" type="button" aria-label="Browse problems" aria-expanded="false" title="Browse problems">
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
            <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="hud-heading">
          <h1>Toy Rocket</h1>
          <p id="root-title" class="root-title"></p>
        </div>
        <div class="hud-totals" aria-label="Tree-wide totals">
          <span id="total-open" class="badge badge-open"></span>
          <span id="total-progress" class="badge badge-progress"></span>
        </div>
        <div class="hud-actions">
          <div class="zoom" role="group" aria-label="Zoom tree">
            <button id="zoom-out" type="button" title="Zoom out">−</button>
            <button id="zoom-reset" type="button" title="Reset zoom"><span id="zoom-level">100%</span></button>
            <button id="zoom-in" type="button" title="Zoom in">+</button>
          </div>${composeActions}
        </div>
      </header>
      <div class="legend" aria-label="Status legend">
        <span><i class="dot dot-open"></i>Open</span>
        <span><i class="dot dot-claimed"></i>In progress</span>
        <span><i class="dot dot-patched"></i>Patched</span>
        <span><i class="dot dot-closed"></i>Closed</span>
      </div>
      <div id="tree-canvas" class="canvas" tabindex="0" aria-label="Problem tree"></div>
      <output id="app-status" class="app-status" aria-live="polite"></output>
    </div>
    ${drawerMarkup()}`;
    bindWorkspace();
    renderHud();
    // A freshly loaded tree starts scrolled to its root; live updates and
    // collapse/expand must not move the view out from under the user.
    renderTreeView(animate, "", true);
  }

  function renderRootList() {
    const list = document.querySelector<HTMLElement>("#root-list");
    if (!list) return;
    if (rootsLoading) {
      list.innerHTML = `<li class="drawer-note">Searching for problem trees…</li>`;
      return;
    }
    if (rootsError) {
      list.innerHTML = `<li class="drawer-note">${escapeHtml(rootsError)} <button type="button" data-roots-retry>Retry</button></li>`;
      return;
    }
    const query = rootSearch.trim().toLocaleLowerCase();
    const visible = (rootSummaries ?? []).filter((summary) =>
      !query || summary.title.toLocaleLowerCase().includes(query) || summary.coordinate.includes(query));
    list.innerHTML = visible.length ? visible.map((summary) => `
    <li>
      <button class="root-item${summary.coordinate === dag?.rootCoordinate ? " current" : ""}" data-root="${summary.coordinate}"
        ${summary.coordinate === dag?.rootCoordinate ? "aria-current=\"true\"" : ""} title="Switch tree root to ${escapeHtml(summary.title)}">
        <span class="root-item-title">${escapeHtml(summary.title)}</span>
        <span class="root-item-meta">
          ${summary.open !== undefined ? `<span class="badge badge-open${summary.open ? "" : " zero"}"><b>${summary.open}</b> open</span>` : ""}
          ${summary.claimed !== undefined ? `<span class="badge badge-progress${summary.claimed ? "" : " zero"}"><b>${summary.claimed}</b> in progress</span>` : ""}
          <span class="root-item-count">${summary.problemCount} problem${summary.problemCount === 1 ? "" : "s"}</span>
        </span>
      </button>
    </li>`).join("")
      : `<li class="drawer-note">${rootSummaries?.length ? "No problem trees match this filter." : `No problem trees found on ${escapeHtml(host.sourceLabel)}.`}</li>`;
  }

  async function discoverRoots() {
    if (rootsLoading) return;
    rootsLoading = true;
    rootsError = "";
    renderRootList();
    try {
      rootSummaries = await host.discoverRoots();
    } catch (error) {
      console.error("Problem tree discovery failed", { error });
      rootsError = error instanceof Error ? error.message : "Problem trees could not be loaded.";
    } finally {
      rootsLoading = false;
      renderRootList();
    }
  }

  function setDrawer(open: boolean) {
    const drawer = document.querySelector<HTMLElement>(".drawer");
    const backdrop = document.querySelector<HTMLElement>(".drawer-backdrop");
    const button = document.querySelector<HTMLButtonElement>("#menu-button");
    if (!drawer || !backdrop || drawerOpen === open) return;
    drawerOpen = open;
    button?.setAttribute("aria-expanded", String(open));
    if (open) {
      renderRootList();
      drawer.hidden = false;
      backdrop.hidden = false;
      if (!reducedMotion.matches) {
        gsap.fromTo(drawer, { xPercent: -104 }, { xPercent: 0, duration: .35, ease: "expo.out" });
        gsap.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: .25, ease: "expo.out" });
      }
      if (!rootSummaries && !rootsLoading) void discoverRoots();
    } else if (!reducedMotion.matches) {
      gsap.to(drawer, { xPercent: -104, duration: .28, ease: "expo.in", onComplete: () => { drawer.hidden = true; } });
      gsap.to(backdrop, { opacity: 0, duration: .28, ease: "expo.in", onComplete: () => { backdrop.hidden = true; } });
    } else {
      drawer.hidden = true;
      backdrop.hidden = true;
    }
  }

  function setZoom(next: number) {
    zoom = next;
    applyZoom(treeCanvas() ?? document, zoom);
  }

  function toggleBranch(coordinate: string) {
    if (!dag) return;
    const expanding = collapsed.has(coordinate);
    if (expanding) collapsed.delete(coordinate);
    else collapsed.add(coordinate);
    renderTreeView(expanding, expanding ? coordinate : "");
    const canvas = treeCanvas();
    if (!expanding && canvas) pulseToggle(canvas, coordinate);
  }

  function updateSelection() {
    if (!dag) return;
    app.querySelectorAll<HTMLButtonElement>(".node-card[data-open]").forEach((button) => {
      const isSelected = button.dataset.open === selected;
      button.classList.toggle("selected", isSelected);
      button.setAttribute("aria-current", String(isSelected));
    });
    renderHud();
  }

  function bindWorkspace() {
    app.oninput = (event) => {
      const input = (event.target as Element).closest<HTMLInputElement>("#root-search");
      if (!input) return;
      rootSearch = input.value;
      renderRootList();
    };
    app.onkeydown = (event) => {
      if (event.key === "Escape" && drawerOpen) setDrawer(false);
    };
    app.onclick = (event) => {
      const target = event.target as Element;
      const toggleButton = target.closest<HTMLButtonElement>("[data-toggle]");
      const openButton = target.closest<HTMLButtonElement>("[data-open]");
      const rootButton = target.closest<HTMLButtonElement>("[data-root]");
      const rocketButton = target.closest<HTMLButtonElement>("[data-rocket]");
      if (target.closest("#menu-button") || target.closest("#browse-problems")) {
        setDrawer(true);
      } else if (target.closest("#drawer-close") || target.closest(".drawer-backdrop")) {
        setDrawer(false);
      } else if (target.closest("[data-roots-retry]")) {
        void discoverRoots();
      } else if (rootButton?.dataset.root) {
        const coordinate = rootButton.dataset.root;
        setDrawer(false);
        if (coordinate !== dag?.rootCoordinate) void loadDag(coordinate);
      } else if (target.closest("#drawer-load")) {
        const input = document.querySelector<HTMLInputElement>("#drawer-coordinate");
        const coordinate = input?.value.trim();
        if (!coordinate) return;
        setDrawer(false);
        void loadDag(coordinate);
      } else if (toggleButton?.dataset.toggle) {
        toggleBranch(toggleButton.dataset.toggle);
      } else if (rocketButton?.dataset.rocket) {
        void openRocket(rocketButton.dataset.rocket);
      } else if (openButton?.dataset.open) {
        selected = openButton.dataset.open;
        updateSelection();
        void openProblem(selected);
      } else if (target.closest("#zoom-in")) {
        setZoom(Math.min(1.6, Math.round((zoom + 0.1) * 10) / 10));
      } else if (target.closest("#zoom-out")) {
        setZoom(Math.max(0.4, Math.round((zoom - 0.1) * 10) / 10));
      } else if (target.closest("#zoom-reset")) {
        setZoom(1);
      } else if (target.closest("#log-child")) {
        void logChildProblem();
      } else if (target.closest("#log-root-child")) {
        void logRootChildProblem();
      }
    };
  }

  function openRocketByCoordinate(coordinate: string) {
    for (const rockets of rocketsForProblems.values()) {
      const rocket = rockets.find((candidate) => candidate.coordinate === coordinate);
      if (rocket) return rocket;
    }
    return undefined;
  }

  async function openRocket(coordinate: string) {
    const rocket = openRocketByCoordinate(coordinate);
    if (!rocket || rocketViewerBusy) return;
    if (!capabilities.viewRocket) {
      if (host.kind === "web") say("Standalone build: the rocket solving this problem is shown read-only. Open this tree inside a Napplet shell to view the rocket.");
      else say("Rocket view is not installed.");
      return;
    }
    rocketViewerBusy = true;
    say(`Opening ${rocket.identifier}…`);
    try {
      const result = await host.viewRocket(rocket);
      if (!result.ok) throw new Error(result.error ?? "No rocket view accepted this request.");
      say("");
    } catch (error) {
      console.error("Rocket view intent failed", { coordinate: rocket.coordinate, error });
      say(error instanceof Error ? error.message : "Rocket view could not be opened.");
    } finally {
      rocketViewerBusy = false;
    }
  }

  async function logRootChildProblem() {
    const root = dag?.nodes.get(dag.rootCoordinate);
    if (!root || childComposerBusy || !canCompose()) return;
    childComposerBusy = true;
    renderHud();
    say("Opening top-level problem composer…");
    try {
      const result = await host.composeTopLevelProblem(root);
      if (!result.ok) throw new Error(result.error ?? "No problem composer accepted this request.");
      say("Top-level problem composer opened.");
    } catch (error) {
      console.error("Top-level problem composer intent failed", { rootCoordinate: root.coordinate, error });
      say(error instanceof Error ? error.message : "Problem composer could not be opened.");
    } finally {
      childComposerBusy = false;
      renderHud();
    }
  }

  async function logChildProblem() {
    const node = dag?.nodes.get(selected);
    if (!node || childComposerBusy || !canCompose()) return;
    childComposerBusy = true;
    renderHud();
    say(`Opening child composer for ${node.title}…`);
    try {
      const result = await host.composeChildProblem(node);
      if (!result.ok) throw new Error(result.error ?? "No problem composer accepted this request.");
      say(`Child composer opened under ${node.title}.`);
      if (collapsed.has(node.coordinate)) toggleBranch(node.coordinate);
    } catch (error) {
      say(error instanceof Error ? error.message : "Problem composer could not be opened.");
    } finally {
      childComposerBusy = false;
      renderHud();
    }
  }

  async function openProblem(coordinate: string) {
    const node = dag?.nodes.get(coordinate);
    if (!node || problemViewerBusy) return;
    if (!capabilities.viewProblem) {
      // The standalone build is a viewer of the tree, not of individual
      // problems; say so once per session instead of nagging on every click.
      if (host.kind === "web" && !readOnlyNoticeShown) {
        readOnlyNoticeShown = true;
        say("Standalone build: selecting problems is local. Open this tree inside a Napplet shell to view a problem.");
      } else if (host.kind === "napplet") {
        say("Problem viewer is not installed.");
      }
      return;
    }
    problemViewerBusy = true;
    say(`Opening ${node.title}…`);
    try {
      const result = await host.viewProblem(node);
      if (!result.ok) throw new Error(result.error ?? "View Problem did not accept this problem.");
      say("");
    } catch (error) {
      say(error instanceof Error ? error.message : "Problem could not be opened.");
    } finally {
      problemViewerBusy = false;
    }
  }

  async function loadDag(value: string) {
    const setupStatus = document.querySelector<HTMLOutputElement>("#setup-status");
    const submit = document.querySelector<HTMLButtonElement>("#root-form button");
    let generation = loadGeneration;
    try {
      const coordinate = assertRootCoordinate(value);
      stopProblemSubscription();
      generation = loadGeneration;
      problemEvents = [];
      dag = undefined;
      collapsed.clear();
      readOnlyNoticeShown = false;
      if (setupStatus) setupStatus.textContent = "Loading problem structure…";
      if (submit) submit.disabled = true;
      say("Loading problem structure…");
      let subscription: EventSubscription | undefined;
      subscription = host.watchTree(coordinate, {
        onEvents: (results) => {
          if (problemSubscription === subscription) receiveProblemEvents(coordinate, results);
        },
        onClosed: (reason) => {
          if (problemSubscription !== subscription) return;
          problemSubscription = undefined;
          console.warn("Live problem tree subscription closed", { rootCoordinate: coordinate, reason });
          say("Live updates stopped. Reload tree to reconnect.");
        }
      });
      problemSubscription = subscription;
      const [events, hostCapabilities, rocketEvents] = await Promise.all([
        host.queryTree(coordinate),
        host.capabilities(),
        // Rocket links are supplementary: a failed sweep must not break the tree.
        host.queryRockets().catch((error) => {
          console.warn("Rocket discovery failed", { rootCoordinate: coordinate, error });
          return [] as RelayEventResult[];
        })
      ]);
      if (generation !== loadGeneration) return;
      capabilities = hostCapabilities;
      rocketsForProblems = rocketsByProblem(rocketsFromEvents(rocketEvents));
      problemEvents = mergeProblemEvents(problemEvents, events).events;
      dag = buildProblemDag(coordinate, problemEvents);
      selected = coordinate;
      zoom = 1;
      host.rememberRoot?.(coordinate);
      renderApp(true);
    } catch (error) {
      if (generation !== loadGeneration) return;
      stopProblemSubscription();
      const message = error instanceof Error ? error.message : "Problem DAG could not be loaded.";
      if (setupStatus) {
        setupStatus.textContent = message;
        if (submit) submit.disabled = false;
      } else if (dag) {
        say(message);
      } else {
        showSetup(message);
      }
    }
  }

  if (options.initialRoot) void loadDag(options.initialRoot);
  else showSetup();

  return { loadRoot: loadDag, showSetup };
}
